import './EntityLogo.scss';
import cn from 'classnames';
import { CDN, CompanyLogoFile, normalizeLogoCid } from '@src/utils/company-logo-constants';
import { Avatar } from '../Avatar';
import { Size } from '../designConstants';
import { Img } from '../Img';

// The circle is smaller than the logo box and centered in it, so rows still line up.
const AVATAR_RATIO = 2 / 3;

// The shared Size scale plus XL for modal/detail headers.
export const EntitySize = { ...Size, XL: 'xlarge' } as const;
export type EntitySizeType = (typeof EntitySize)[keyof typeof EntitySize];

const LOGO_PX: Record<EntitySizeType, number> = {
  [EntitySize.SM]: 24,
  [EntitySize.MD]: 32,
  [EntitySize.LG]: 40,
  [EntitySize.XL]: 48
};

export const EntityKind = {
  COMPANY: 'company',
  NETWORK: 'network'
} as const;
export type EntityKindType = (typeof EntityKind)[keyof typeof EntityKind];

export const LogoVariant = {
  SQUARE: 'square',
  WIDE: 'wide'
} as const;
export type LogoVariantType = (typeof LogoVariant)[keyof typeof LogoVariant];

const COMPANY_LOGO_FILE: Record<LogoVariantType, string> = {
  [LogoVariant.SQUARE]: CompanyLogoFile.SQUARE_LOGO,
  [LogoVariant.WIDE]: CompanyLogoFile.LOGO
};

const getLogoSrc = (kind: EntityKindType, id: string, variant: LogoVariantType): string =>
  kind === EntityKind.NETWORK
    ? `${CDN}/networks/${id}.png`
    : `${CDN}/companies/${normalizeLogoCid(id)}/${COMPANY_LOGO_FILE[variant]}`;

interface EntityLogoProps {
  kind: EntityKindType;
  // Company cid or network short code.
  id: string;
  size?: EntitySizeType;
  // Company only: WIDE uses logo.png instead of square_logo.png. Either way it is contained in the size's square box.
  variant?: LogoVariantType;
  // Display name for the image's alt and tooltip. Defaults to the id.
  name?: string;
  // Text shown next to the logo (the logo + name cell).
  label?: string;
  // Show the id beside the fallback avatar when the logo fails to load.
  showFallbackName?: boolean;
  className?: string;
}

// The one place a company/network logo is built: fixed box per size token, logo contained inside it, avatar when the image is missing.
export const EntityLogo = ({
  kind,
  id,
  size = Size.MD,
  variant = LogoVariant.SQUARE,
  name = id,
  label,
  showFallbackName = false,
  className
}: EntityLogoProps) => {
  const px = LOGO_PX[size];
  return (
    <span className={cn('entity-logo', className)}>
      {/* One box for the spinner, image and fallback, so the cell doesn't shift as the image loads. */}
      <span className='entity-logo-box' style={{ minWidth: px, height: px }}>
        <Img
          className='entity-logo-img'
          src={getLogoSrc(kind, id, variant)}
          // The label already names the entity, so the image is decorative next to it.
          alt={label ? '' : name}
          title={label ? undefined : name}
          style={{ height: px, width: px }}
          unloader={
            <span className='entity-logo-fallback'>
              <Avatar name={id} size={Math.round(px * AVATAR_RATIO)} />
              {showFallbackName && <span className='entity-logo-name'>{id}</span>}
            </span>
          }
        />
      </span>
      {label && <span className='entity-logo-name'>{label}</span>}
    </span>
  );
};
